import { AttachmentRule, Behavior } from "./types";
import { WorldMaker } from "../WorldMaker";

export class PlayerLifecycleBehavior extends Behavior {
  // Static registry to track all spawned characters across all instances
  private static globalSpawnedCharacters: Map<string, Element> = new Map();

  private playerTemplate: string = "";
  private spawnedCharacters: Map<string, Element> = new Map();
  private connectionListener: (event: Event) => void;
  private disconnectionListener: (event: Event) => void;
  private respawnDelay: number = 3000; // Default 3 seconds
  private respawnTimers: Map<string, number> = new Map();
  private mutationObserver: MutationObserver | null = null;
  private deadPlayers: Set<string> = new Set();

  static getAttachmentRules(): AttachmentRule[] {
    return [{ type: "class", selector: "player-lifecycle" }];
  }

  /**
   * Static helper method to look up an m-character element by connection ID
   * @param connectionId The connection ID to look up
   * @returns The m-character element if found, null otherwise
   */
  public static getCharacterByConnectionId(connectionId: string): Element | null {
    return this.globalSpawnedCharacters.get(connectionId) || null;
  }

  /**
   * Static helper method to get all active player characters
   * @returns Map of connection IDs to m-character elements
   */
  public static getAllCharacters(): Map<string, Element> {
    return new Map(this.globalSpawnedCharacters);
  }

  constructor(element: Element, world: WorldMaker) {
    super(element, world);

    // Bind event listeners to maintain context
    this.connectionListener = this.handlePlayerConnection.bind(this);
    this.disconnectionListener = this.handlePlayerDisconnection.bind(this);

    // Get respawn delay from element attribute
    const delayAttr = this.element.getAttribute("respawn-delay");
    if (delayAttr) {
      const delay = parseInt(delayAttr, 10);
      if (!isNaN(delay) && delay >= 0) {
        this.respawnDelay = delay;
      }
    }
  }

  onAttach(): void {
    console.log("PlayerLifecycleBehavior attached to:", this.element);

    // Immediately set visible-to="-1" to hide from all players
    this.element.setAttribute("visible-to", "-1");
    console.log('PlayerLifecycleBehavior: Set visible-to="-1" to hide from players');

    // Capture the m-character template
    const templateElement = this.element.firstElementChild;
    if (templateElement) {
      this.playerTemplate = templateElement.outerHTML;
      console.log("PlayerLifecycleBehavior: Captured player template:", this.playerTemplate);
    } else {
      console.warn("PlayerLifecycleBehavior: No child template element found in element");
    }

    // Add event listeners for player lifecycle events
    window.addEventListener("connected", this.connectionListener);
    window.addEventListener("disconnected", this.disconnectionListener);

    // Set up mutation observer to detect character removal (death)
    this.setupDeathDetection();

    console.log("PlayerLifecycleBehavior: Listening for player connection events");
    console.log(`PlayerLifecycleBehavior: Respawn delay set to ${this.respawnDelay}ms`);
  }

  onDetach(): void {
    console.log("PlayerLifecycleBehavior detached from:", this.element);

    // Remove event listeners
    window.removeEventListener("connected", this.connectionListener);
    window.removeEventListener("disconnected", this.disconnectionListener);

    // Clean up mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Clear all respawn timers
    this.respawnTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.respawnTimers.clear();

    // Clean up any spawned characters
    this.spawnedCharacters.forEach((character, connectionId) => {
      this.removePlayerCharacter(connectionId, false);
    });

    console.log("PlayerLifecycleBehavior: Cleaned up event listeners and spawned characters");
  }

  private setupDeathDetection(): void {
    const parent = this.element.parentElement;
    if (!parent) {
      console.warn("PlayerLifecycleBehavior: No parent element found for death detection");
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.tagName.toLowerCase() === "m-character") {
                // Check if this was one of our spawned characters
                for (const [connectionId, character] of this.spawnedCharacters.entries()) {
                  if (character === element) {
                    console.log(
                      `PlayerLifecycleBehavior: Character died for player ${connectionId}`,
                    );
                    this.handlePlayerDeath(connectionId);
                    break;
                  }
                }
              }
            }
          });
        }
      });
    });

    this.mutationObserver.observe(parent, {
      childList: true,
      subtree: true,
    });
  }

  private handlePlayerConnection(event: Event): void {
    const customEvent = event as CustomEvent;
    const connectionId = customEvent.detail?.connectionId;
    if (!connectionId) {
      console.warn("PlayerLifecycleBehavior: No connectionId in connection event");
      return;
    }

    console.log(`PlayerLifecycleBehavior: Player connected with ID: ${connectionId}`);

    // Clear any existing respawn timer for this player
    this.clearRespawnTimer(connectionId);

    // Remove from dead players set
    this.deadPlayers.delete(connectionId);

    this.spawnPlayerCharacter(connectionId);
  }

  private handlePlayerDisconnection(event: Event): void {
    const customEvent = event as CustomEvent;
    const connectionId = customEvent.detail?.connectionId;
    if (!connectionId) {
      console.warn("PlayerLifecycleBehavior: No connectionId in disconnection event");
      return;
    }

    console.log(`PlayerLifecycleBehavior: Player disconnected with ID: ${connectionId}`);

    // Clear any existing respawn timer
    this.clearRespawnTimer(connectionId);

    // Remove from dead players set
    this.deadPlayers.delete(connectionId);

    this.removePlayerCharacter(connectionId, false);
  }

  private handlePlayerDeath(connectionId: string): void {
    console.log(`PlayerLifecycleBehavior: Handling death for player ${connectionId}`);

    // Remove the character from our tracking (it's already been removed from DOM)
    this.spawnedCharacters.delete(connectionId);

    // Also remove from the static registry
    PlayerLifecycleBehavior.globalSpawnedCharacters.delete(connectionId);

    // Mark player as dead
    this.deadPlayers.add(connectionId);

    // Clear any existing respawn timer
    this.clearRespawnTimer(connectionId);

    // Set up respawn timer
    if (this.respawnDelay > 0) {
      console.log(
        `PlayerLifecycleBehavior: Scheduling respawn for player ${connectionId} in ${this.respawnDelay}ms`,
      );
      const timer = setTimeout(() => {
        this.respawnPlayer(connectionId);
      }, this.respawnDelay);

      this.respawnTimers.set(connectionId, timer);
    } else {
      // Immediate respawn if delay is 0
      this.respawnPlayer(connectionId);
    }
  }

  private respawnPlayer(connectionId: string): void {
    // Check if player is still dead (hasn't reconnected)
    if (!this.deadPlayers.has(connectionId)) {
      console.log(
        `PlayerLifecycleBehavior: Player ${connectionId} is no longer dead, skipping respawn`,
      );
      return;
    }

    console.log(`PlayerLifecycleBehavior: Respawning player ${connectionId}`);

    // Remove from dead players set
    this.deadPlayers.delete(connectionId);

    // Clear the timer
    this.clearRespawnTimer(connectionId);

    // Spawn the character
    this.spawnPlayerCharacter(connectionId);
  }

  private clearRespawnTimer(connectionId: string): void {
    const timer = this.respawnTimers.get(connectionId);
    if (timer) {
      clearTimeout(timer);
      this.respawnTimers.delete(connectionId);
    }
  }

  private spawnPlayerCharacter(connectionId: string): void {
    const parent = this.element.parentElement;
    if (!parent) {
      console.warn("PlayerLifecycleBehavior: No parent element found for spawning");
      return;
    }

    if (this.playerTemplate.trim() === "") {
      console.warn("PlayerLifecycleBehavior: No player template to spawn");
      return;
    }

    // Check if character already exists for this connection
    if (this.spawnedCharacters.has(connectionId)) {
      console.warn(
        `PlayerLifecycleBehavior: Character already exists for connection ${connectionId}`,
      );
      return;
    }

    // Create a temporary container to parse the template HTML
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = this.playerTemplate;

    const elementToSpawn = tempContainer.firstElementChild;
    if (!elementToSpawn) {
      console.warn("PlayerLifecycleBehavior: No element found in template");
      return;
    }

    // Clone the character element
    const clonedCharacter = elementToSpawn.cloneNode(true) as Element;

    // Create and add the m-control-probe element
    const controlProbe = document.createElement("m-control-probe");
    // controlProbe.setAttribute("y", "1.5");
    controlProbe.setAttribute("visible-to", connectionId);
    clonedCharacter.appendChild(controlProbe);

    // Add the character to the parent
    parent.appendChild(clonedCharacter);

    // Store reference to the spawned character
    this.spawnedCharacters.set(connectionId, clonedCharacter);

    // Also update the static registry
    PlayerLifecycleBehavior.globalSpawnedCharacters.set(connectionId, clonedCharacter);

    // Trigger behavior attachment for the new element if the world has an attach method
    if (this.world && typeof (this.world as any).attachBehaviorsToElement === "function") {
      (this.world as any).attachBehaviorsToElement(clonedCharacter);
    }

    console.log(`PlayerLifecycleBehavior: Spawned character for player ${connectionId}`);
  }

  private removePlayerCharacter(connectionId: string, isDeath: boolean = false): void {
    const character = this.spawnedCharacters.get(connectionId);
    if (!character) {
      console.warn(`PlayerLifecycleBehavior: No character found for connection ${connectionId}`);
      return;
    }

    // Remove the character from the DOM only if it's not a death (death already removed it)
    if (!isDeath && character.parentElement) {
      character.parentElement.removeChild(character);
    }

    // Remove from our tracking map
    this.spawnedCharacters.delete(connectionId);

    // Also remove from the static registry
    PlayerLifecycleBehavior.globalSpawnedCharacters.delete(connectionId);

    console.log(`PlayerLifecycleBehavior: Removed character for player ${connectionId}`);
  }

  tick(deltaTime: number): void {
    // No continuous updates needed for this behavior
  }
}
